import { checkmarkCircle, alertCircleOutline } from 'ionicons/icons';
import type { Contact } from '@rsmaids/shared/types/contacts';

type Tr = (key: string, opts?: Record<string, unknown>) => string;

// Ids on phones/emails/addresses/custom fields are generated per load, so they never count as a change; an empty
// value and a missing one are the same thing.
const normalized = (v: unknown) => JSON.stringify(v ?? null, (key, x) => {
  if (key === 'id' || key === '_id') return undefined;
  if (x === '' || (Array.isArray(x) && x.length === 0)) return null;
  return x;
});

// The lists are compared by what they SAY (digits, address, value), not by their stored shape, so a different
// format of the same phone or address is not reported as a change.
const digits = (v: unknown) => String(v ?? '').replace(/\D/g, '');
const lower = (v: unknown) => String(v ?? '').trim().toLowerCase();
const valueSig = (key: string, v: unknown): string => {
  const list = (Array.isArray(v) ? v : []) as Array<Record<string, unknown>>;
  switch (key) {
    case 'phones': return JSON.stringify(list.map(p => digits(p['phone'])).filter(Boolean).sort());
    case 'emails': return JSON.stringify(list.map(e => lower(e['email'])).filter(Boolean).sort());
    case 'addresses': return JSON.stringify(list.map(a => {
      const ad = (a['address'] ?? {}) as Record<string, unknown>;
      return [ad['addressLine1'], ad['addressLine2'], ad['city'], ad['postalCode']].map(lower).join('|');
    }).sort());
    case 'customFields': return JSON.stringify(list.map(f => `${lower(f['fieldKey'] ?? f['label'])}|${lower(f['value'])}`).sort());
    case 'labels': return JSON.stringify((Array.isArray(v) ? v : []).map(String).sort());
    default: return normalized(v);
  }
};

// Contact fields as the user knows them → the Contact properties that make them up.
const FIELD_GROUPS: Array<[label: string, keys: string[]]> = [
  ['name', ['name', 'firstName', 'lastName']],
  ['company', ['company']],
  ['jobTitle', ['jobTitle', 'position']],
  ['birthdate', ['birthdate']],
  ['phones', ['phones']],
  ['emails', ['emails']],
  ['addresses', ['addresses']],
  ['customFields', ['customFields']],
  ['labels', ['labels']],
  ['displayNameSource', ['displayNameSource']],
];

// Written out in full (not built from a prefix) so the unused-key cleanup in the i18n tooling can see they're used.
const FIELD_LABEL_KEYS: Record<string, string> = {
  name: 'pages.bookingSystem.Contact.field_name',
  company: 'pages.bookingSystem.Contact.field_company',
  jobTitle: 'pages.bookingSystem.Contact.field_jobTitle',
  birthdate: 'pages.bookingSystem.Contact.field_birthdate',
  phones: 'pages.bookingSystem.Contact.field_phones',
  emails: 'pages.bookingSystem.Contact.field_emails',
  addresses: 'pages.bookingSystem.Contact.field_addresses',
  customFields: 'pages.bookingSystem.Contact.field_customFields',
  labels: 'pages.bookingSystem.Contact.field_labels',
  displayNameSource: 'pages.bookingSystem.Contact.field_displayNameSource',
};

type ChangeKind = 'added' | 'removed' | 'updated';

// Fields that hold a list (more items = something was added, fewer = removed); the rest hold a single value.
const LIST_KEYS = new Set(['phones', 'emails', 'addresses', 'customFields', 'labels']);

/** Which user-facing fields changed between two versions of a contact, and whether each was added, removed or updated. */
export function contactChanges(before: Contact, after: Contact): Array<{ field: string; kind: ChangeKind }> {
  const b = before as unknown as Record<string, unknown>;
  const a = after as unknown as Record<string, unknown>;
  const out: Array<{ field: string; kind: ChangeKind }> = [];
  for (const [field, keys] of FIELD_GROUPS) {
    if (!keys.some(k => valueSig(k, b[k]) !== valueSig(k, a[k]))) continue;
    let kind: ChangeKind = 'updated';
    if (LIST_KEYS.has(field)) {
      const count = (rec: Record<string, unknown>) => (JSON.parse(valueSig(field, rec[field])) as unknown[]).length;
      const was = count(b);
      const now = count(a);
      kind = now > was ? 'added' : now < was ? 'removed' : 'updated';
    } else {
      const has = (rec: Record<string, unknown>) => keys.some(k => normalized(rec[k]) !== 'null');
      const was = has(b);
      const now = has(a);
      kind = !was && now ? 'added' : was && !now ? 'removed' : 'updated';
    }
    out.push({ field, kind });
  }
  return out;
}

/**
 * The app's standard success toast, naming what happened to what: "Added: Phone", "Removed: Address",
 * "Updated: Email" — several kinds in one save read "Removed: Address; Updated: Email". Worded as a label plus a
 * list so it reads correctly in Spanish and Portuguese, where the word for "removed" changes with the noun's gender.
 */
export function contactSavedToast(before: Contact, after: Contact, tr: Tr) {
  const changes = contactChanges(before, after);
  const part = (kind: ChangeKind, key: string) => {
    const fields = changes.filter(c => c.kind === kind).map(c => tr(FIELD_LABEL_KEYS[c.field]));
    return fields.length ? tr(key, { fields: fields.join(', ') }) : '';
  };
  const message = [
    part('added', 'pages.bookingSystem.Contact.addedFields'),
    part('removed', 'pages.bookingSystem.Contact.removedFields'),
    part('updated', 'pages.bookingSystem.Contact.updatedFields'),
  ].filter(Boolean).join('; ') || tr('pages.bookingSystem.Contact.contactSaved');
  return { message, duration: 2500, position: 'bottom' as const, cssClass: 'app-toast', icon: checkmarkCircle };
}

/** The app's standard error toast. */
export function contactErrorToast(message: string) {
  return { message, duration: 4000, position: 'top' as const, cssClass: 'app-toast app-toast--error', icon: alertCircleOutline };
}
